// Plan-features parsing for ioX-Bridge tenants. The Subscription Manager
// seeds each ioX-Bridge tenant with a flat set of `iox:*` tags describing
// which tier the customer paid for (see ioX-Subscription-Manager's
// buildTenantTags). This module is the single source of truth for turning
// those tags into typed knobs the UI can gate on.
//
// Every gate defaults to the most permissive interpretation when a tag is
// missing — the fork predates per-tenant licensing, and existing tenants
// that pre-date the SM wiring mustn't be suddenly locked out of features
// they were using. The only tags we actively restrict on are the ones SM
// explicitly writes; anything absent = "unlimited / allowed".
//
// Server enforcement lives in Phase 3 (Rust); this UI gating is defence
// in depth only. A customer with a valid tenant API key can still call
// gRPC directly and exceed caps until the server enforces them.

import { IntegrationKind } from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import { Region } from "@chirpstack/chirpstack-api-grpc-web/common/common_pb";

// Tag keys — match what SM writes. Keep in sync with
// ioX-Subscription-Manager/server/src/utils/chirpstack.js:buildTenantTags
// which prefixes every ProductFeatures row with `iox:`.
export const IOX_TAG_PLAN_CODE = "iox:planCode";
export const IOX_TAG_MAX_APPLICATIONS = "iox:maxApplications";
export const IOX_TAG_ALLOW_GATEWAYS = "iox:allowGateways";
export const IOX_TAG_MAX_GATEWAY_COUNT = "iox:maxGatewayCount";
export const IOX_TAG_GATEWAYS_PRIVATE_UPLINK = "iox:gatewaysPrivateUplink";
export const IOX_TAG_GATEWAYS_PRIVATE_DOWNLINK = "iox:gatewaysPrivateDownlink";
export const IOX_TAG_ALLOW_SERVER_DEVICE_PROFILES = "iox:allowServerDeviceProfiles";
export const IOX_TAG_ALLOWED_INTEGRATIONS = "iox:allowedIntegrations";
export const IOX_TAG_ALLOWED_REGIONS = "iox:allowedRegions";

// Canonical uppercase keys matching SM's seed. When SM says
// `allowedRegions = "EU868,US915,AU915"` we compare each Region enum
// value against these strings. Keys match ioX-Bridge's Region enum
// names exactly — see chirpstack-api/common/common.proto.
const REGION_KEY: Record<number, string> = {
  [Region.EU868]: "EU868",
  [Region.US915]: "US915",
  [Region.CN779]: "CN779",
  [Region.EU433]: "EU433",
  [Region.AU915]: "AU915",
  [Region.CN470]: "CN470",
  [Region.AS923]: "AS923",
  [Region.AS923_2]: "AS923",
  [Region.AS923_3]: "AS923",
  [Region.AS923_4]: "AS923",
  [Region.KR920]: "KR920",
  [Region.IN865]: "IN865",
  [Region.RU864]: "RU864",
  [Region.ISM2400]: "ISM2400",
};

// Canonical lowercase keys matching SM's seed. When SM says
// `allowedIntegrations = "http,mqtt,aws_sns,azure_service_bus"` we compare
// each IntegrationKind against these strings.
const INTEGRATION_KIND_KEY: Record<number, string> = {
  [IntegrationKind.HTTP]: "http",
  [IntegrationKind.MQTT_GLOBAL]: "mqtt",
  [IntegrationKind.AWS_SNS]: "aws_sns",
  [IntegrationKind.AZURE_SERVICE_BUS]: "azure_service_bus",
  [IntegrationKind.GCP_PUB_SUB]: "gcp_pub_sub",
  [IntegrationKind.INFLUX_DB]: "influxdb",
  [IntegrationKind.THINGS_BOARD]: "thingsboard",
  [IntegrationKind.MY_DEVICES]: "mydevices",
  [IntegrationKind.PILOT_THINGS]: "pilot_things",
  [IntegrationKind.BLYNK]: "blynk",
  [IntegrationKind.IFTTT]: "ifttt",
};

export type AllowedIntegrations = "*" | ReadonlySet<string>;
export type AllowedRegions = "*" | ReadonlySet<string>;

export interface PlanFeatures {
  /** e.g. "bridge-pro". Undefined when the tenant has no SM-managed tags. */
  planCode: string | undefined;
  /** 0 means unlimited. */
  maxApplications: number;
  allowGateways: boolean;
  /** 0 means unlimited. */
  maxGatewayCount: number;
  gatewaysPrivateUplink: boolean;
  gatewaysPrivateDownlink: boolean;
  /** Pro-only: access to EAMS-authored server-level device profiles. */
  allowServerDeviceProfiles: boolean;
  /** "*" = all integrations allowed; Set = whitelist of lowercase keys. */
  allowedIntegrations: AllowedIntegrations;
  /**
   * "*" = all LoRaWAN regions allowed; Set = whitelist of Region enum
   * names (e.g. "EU868", "US915"). Filters vendor device-profile
   * templates and the CreateDeviceProfile region dropdown. AS923
   * variants (AS923_2/3/4) all map to the "AS923" key — a plan
   * entitlement applies to the family, not per-frequency-plan.
   */
  allowedRegions: AllowedRegions;
  /**
   * Distinguish "SM has taken responsibility for this tenant" from
   * "tenant pre-dates SM wiring". When false, all gates effectively
   * behave as `allow everything` — see file-level comment for why.
   */
  hasPlanTags: boolean;
}

/** Permissive default used when a tenant has no SM-managed tags. */
export const PLAN_FEATURES_UNRESTRICTED: PlanFeatures = {
  planCode: undefined,
  maxApplications: 0,
  allowGateways: true,
  maxGatewayCount: 0,
  gatewaysPrivateUplink: false,
  gatewaysPrivateDownlink: false,
  allowServerDeviceProfiles: true,
  allowedIntegrations: "*",
  allowedRegions: "*",
  hasPlanTags: false,
};

function parseBool(v: string | undefined, fallback: boolean): boolean {
  if (v === undefined) return fallback;
  return v.trim().toLowerCase() === "true";
}

function parseInt0(v: string | undefined, fallback: number): number {
  if (v === undefined) return fallback;
  const n = Number.parseInt(v.trim(), 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function parseAllowedIntegrations(v: string | undefined): AllowedIntegrations {
  if (v === undefined) return "*";
  const trimmed = v.trim();
  if (trimmed === "" || trimmed === "*") return "*";
  const keys = trimmed
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return new Set(keys);
}

function parseAllowedRegions(v: string | undefined): AllowedRegions {
  if (v === undefined) return "*";
  const trimmed = v.trim();
  if (trimmed === "" || trimmed === "*") return "*";
  const keys = trimmed
    // Region names are uppercase in ioX-Bridge's enum; normalise on
    // read so a human-typed "eu868" still matches. Values are validated
    // against REGION_KEY at compare time — unknown strings just never
    // match any region and thus effectively block everything, which is
    // the safe outcome for a malformed tag.
    .split(",")
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);
  return new Set(keys);
}

/**
 * Parse the plan-feature tags off a tenant tag map. Accepts the raw
 * `Map<string, string>` that ioX-Bridge's protobuf returns from
 * `tenant.getTagsMap()`.
 */
export function parsePlanFeatures(tagsMap: Map<string, string> | undefined): PlanFeatures {
  if (!tagsMap) return PLAN_FEATURES_UNRESTRICTED;

  const get = (key: string) => tagsMap.get(key);
  const planCode = get(IOX_TAG_PLAN_CODE);
  const hasPlanTags = Boolean(planCode) || tagsMap.has(IOX_TAG_ALLOWED_INTEGRATIONS);

  if (!hasPlanTags) return PLAN_FEATURES_UNRESTRICTED;

  return {
    planCode,
    maxApplications: parseInt0(get(IOX_TAG_MAX_APPLICATIONS), 0),
    allowGateways: parseBool(get(IOX_TAG_ALLOW_GATEWAYS), true),
    maxGatewayCount: parseInt0(get(IOX_TAG_MAX_GATEWAY_COUNT), 0),
    gatewaysPrivateUplink: parseBool(get(IOX_TAG_GATEWAYS_PRIVATE_UPLINK), false),
    gatewaysPrivateDownlink: parseBool(get(IOX_TAG_GATEWAYS_PRIVATE_DOWNLINK), false),
    allowServerDeviceProfiles: parseBool(get(IOX_TAG_ALLOW_SERVER_DEVICE_PROFILES), false),
    allowedIntegrations: parseAllowedIntegrations(get(IOX_TAG_ALLOWED_INTEGRATIONS)),
    allowedRegions: parseAllowedRegions(get(IOX_TAG_ALLOWED_REGIONS)),
    hasPlanTags: true,
  };
}

/**
 * Is this IntegrationKind included in the plan's allowedIntegrations?
 * Unknown kinds (not in our mapping) are allowed when the plan is "*"
 * and blocked otherwise — matches the whitelist default.
 */
export function isIntegrationAllowed(
  allowed: AllowedIntegrations,
  kind: IntegrationKind,
): boolean {
  if (allowed === "*") return true;
  const key = INTEGRATION_KIND_KEY[kind];
  if (!key) return false;
  return allowed.has(key);
}

/**
 * Is this Region included in the plan's allowedRegions? Unknown
 * region values (not in our mapping) are allowed when the plan is
 * "*" and blocked otherwise — matches the whitelist default.
 * AS923 variants all collapse to the "AS923" key by design.
 */
export function isRegionAllowed(
  allowed: AllowedRegions,
  region: Region,
): boolean {
  if (allowed === "*") return true;
  const key = REGION_KEY[region];
  if (!key) return false;
  return allowed.has(key);
}
